/**
 * Business logic hook - derives all data from raw query results
 * Handles data transformation (no useMemo for trivial access - trust React Compiler)
 */
import { preprocessReviews } from '../../lib'

import type {
  ProductAttributes,
  ProductDescription,
  ProductRelated,
  AutoRelatedProduct,
  RelatedProductItem,
  CustomAttribute,
  ProductCategory,
} from '../../types'

interface UseProductActionsParams {
  productAttributes: ProductAttributes | null
  productDescription: ProductDescription | null
  productRelated: ProductRelated | null
  autoRelatedProducts: AutoRelatedProduct[]
  rewardPoints: number
}

export interface ProductActionsResult {
  productName: string | null
  description: string | null
  customAttributes: CustomAttribute[] | null
  categories: ProductCategory[] | null
  upsellProducts: RelatedProductItem[]
  relatedProducts: RelatedProductItem[]
  crossSellProducts: RelatedProductItem[]
  autoRelatedProducts: AutoRelatedProduct[]
  rewardPoints: number
  averageStars: number
  numberOfReviews: number
}

function computeReviewStats(productAttributes: ProductAttributes | null): {
  averageStars: number
  numberOfReviews: number
} {
  const reviews = preprocessReviews(productAttributes?.reviews?.items ?? [])
  const count = reviews.length

  if (count === 0) {
    return { averageStars: 0, numberOfReviews: 0 }
  }

  const totalStarsValue = reviews.reduce((sum, review) => {
    const reviewStars =
      review.ratings_breakdown.reduce((rSum, rating) => rSum + Number(rating.value), 0) /
      review.ratings_breakdown.length
    return sum + reviewStars
  }, 0)

  return {
    averageStars: parseFloat((totalStarsValue / count).toFixed(1)),
    numberOfReviews: count,
  }
}

export function useProductActions({
  productAttributes,
  productDescription,
  productRelated,
  autoRelatedProducts,
  rewardPoints,
}: UseProductActionsParams): ProductActionsResult {
  // Trivial property access - React Compiler handles memoization
  const upsellProducts = productRelated?.upsell_products ?? []
  const relatedProducts = productRelated?.related_products ?? []
  const crossSellProducts = productRelated?.crosssell_products ?? []
  const productName = productAttributes?.name ?? null
  const description = productDescription?.description?.html ?? null
  const customAttributes = productAttributes?.custom_attributes ?? null
  const categories = productAttributes?.categories ?? null

  const { averageStars, numberOfReviews } = computeReviewStats(productAttributes)

  return {
    productName,
    description,
    customAttributes,
    categories,
    upsellProducts,
    relatedProducts,
    crossSellProducts,
    autoRelatedProducts,
    rewardPoints,
    averageStars,
    numberOfReviews,
  }
}
